import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";
import { api, money } from "../lib/api";
import { MerchImage } from "../components/site/MerchImage";
import { useCart } from "../lib/cart";
import { PageTitle } from "../components/site/PageTitle";

const MerchCard = ({ m }) => {
  const { add, setOpen } = useCart();
  const [size, setSize] = useState("");
  const needsSize = m.sizes.length > 0;

  const addToCart = () => {
    if (needsSize && !size) return toast.error("Pick a size first");
    add({ ref_id: m.id, name: m.name, price: m.price, image_url: m.image_url, size: size || null, quantity: 1 });
    toast.success(`${m.name} added to cart`, { action: { label: "View cart", onClick: () => setOpen(true) } });
  };

  return (
    <div data-testid={`merch-card-${m.id}`} className="reveal">
      <div className="img-zoom aspect-[4/5] overflow-hidden rounded-2xl card-surface">
        <MerchImage m={m} />
      </div>
      <div className="mt-4 flex justify-between items-baseline gap-4">
        <h3 className="font-cond uppercase font-bold text-2xl tracking-wide">{m.name}</h3>
        <span className="font-mono-ui text-lime">{money(m.price)}</span>
      </div>
      <p className="text-sm text-slate-400 mt-1">{m.description}</p>
      {needsSize && (
        <div className="flex flex-wrap gap-2 mt-4" data-testid={`merch-sizes-${m.id}`}>
          {m.sizes.map((s) => (
            <button key={s} onClick={() => setSize(s)} data-testid={`merch-size-${m.id}-${s}`}
              className={`btn-line rounded-md min-w-[44px] px-2 py-1.5 text-sm font-mono-ui ${size === s ? "!border-[#00d436] text-lime" : "text-slate-300"}`}>
              {s}
            </button>
          ))}
        </div>
      )}
      <button onClick={addToCart} data-testid={`merch-add-button-${m.id}`}
        className="btn-lime mt-5 w-full rounded-full py-3 font-cond uppercase font-bold tracking-widest inline-flex items-center justify-center gap-2">
        <ShoppingBag className="w-4 h-4" /> Add to cart
      </button>
    </div>
  );
};

export default function MerchPage() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("All");
  useEffect(() => {
    api.merch().then(setItems).catch(() => setItems([]));
  }, []);
  const cats = ["All", ...new Set((items || []).map((m) => m.category))];
  const shown = (items || []).filter((m) => cat === "All" || m.category === cat);

  return (
    <>
      <PageTitle kicker="Official store" title="Merch" />
      <div className={`px-5 lg:px-12 flex flex-wrap gap-3 mb-10 ${items && items.length === 0 ? "hidden" : ""}`} data-testid="merch-category-filter">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`merch-cat-${c}`}
            className={`rounded-full px-5 py-2 font-cond uppercase tracking-widest text-sm ${cat === c ? "bg-lime text-black" : "btn-line text-slate-300"}`}>
            {c}
          </button>
        ))}
      </div>
      <section className="px-5 lg:px-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
        {items && shown.length === 0 && (
          <div data-testid="merch-empty" className="col-span-full card-surface rounded-2xl px-6 py-14 text-center">
            <p className="font-gothic text-6xl text-lime">TBD</p>
            <p className="font-cond uppercase tracking-[0.3em] text-sm text-slate-400 mt-3">Merch coming soon</p>
          </div>
        )}
        {shown.map((m) => <MerchCard key={m.id} m={m} />)}
      </section>
    </>
  );
}
